import {
  TextField,
  capitalize,
  Box,
  ClickAwayListener,
  SelectChangeEvent,
  ClickAwayListenerProps,
} from "@mui/material";
import { motion, Variants } from "motion/react";
import { useAppNavDrawer } from "~/providers/app-nav-drawer";
import { IconButton, InputAdornment, MenuItem, Select } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { SearchScope } from "../types";
import GlobalSearchIcon from "~/components/AppNavToolbar/AppSearch/GlobalSearchSm/GlobalSearchIcon";
import { KeyboardHints } from "~/components/KeyboardHints/KeyboardHints";
import { useScope, ScopeType } from "~/hooks/useScope";

interface GlobalSearchInputProps {
  isFocused: boolean;
  inputRef: React.Ref<HTMLInputElement>;
  scope: SearchScope;
  TextFieldProps: {
    query: string;
    handleFocus:
      | React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
      | undefined;
    handleChange:
      | React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
      | undefined;
    handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> | undefined;
    handleToggleFocus: React.MouseEventHandler<HTMLButtonElement>;
    handleClickAway: ClickAwayListenerProps["onClickAway"];
  };
  EndAdornmentProps: {
    handleClear: React.MouseEventHandler<HTMLButtonElement> | undefined;
    handleScopeChange: (event: SelectChangeEvent<SearchScope>) => void;
    spaceId: string | null;
  };
}

export default function GlobalSearchInput({
  isFocused,
  inputRef,
  scope,
  TextFieldProps: {
    query,
    handleFocus,
    handleChange,
    handleKeyDown,
    handleToggleFocus,
    handleClickAway,
  },
  EndAdornmentProps: { handleClear, handleScopeChange, spaceId },
}: GlobalSearchInputProps) {
  const navDrawer = useAppNavDrawer();
  const { activeScope } = useScope();

  const showSpaceOption =
    activeScope?.type === ScopeType.Space ||
    activeScope?.type === ScopeType.Workspace;
  const showWorkspaceOption = activeScope?.type === ScopeType.Workspace;

  const focusedWidth = "600px";
  const blurredWidth = "0";

  const variants: Variants = {
    closed: {
      opacity: 0,
      pointerEvents: "none",
      width: blurredWidth,
    },
    open: {
      opacity: 1,
      pointerEvents: "auto",
      width: focusedWidth,
    },
  };

  const endAdornment = (
    <InputAdornment position="end">
      <IconButton
        size="small"
        onClick={handleClear}
        aria-label="global search"
        aria-controls="search-dropdown"
        aria-expanded={isFocused}
        sx={{ mr: 0.5 }}
      >
        <CloseIcon fontSize="inherit" />
      </IconButton>
      <Select
        value={scope}
        renderValue={(v) => `In ${v}`}
        onChange={handleScopeChange}
        size="small"
        variant="standard"
        disableUnderline
        slotProps={{
          input: {
            "aria-label": "Search scope",
          },
        }}
        MenuProps={{
          disablePortal: true,
          anchorOrigin: { horizontal: "right", vertical: "bottom" },
          transformOrigin: { horizontal: "right", vertical: "top" },
          sx: {
            ".MuiMenu-paper": { borderTopRightRadius: 0, mt: "1px" },
            ".MuiMenuItem-root": { fontSize: "12px" },
          },
        }}
        disabled={!spaceId}
        sx={{
          "&.MuiInputBase-root": {
            backgroundColor: "background.paper",
            borderLeft: "1px solid",
            borderLeftColor: "divider",
          },
          ".MuiSelect-select": {
            display: "flex",
            height: "36px",
            fontSize: "12px",
            fontWeight: 700,
            textTransform: "uppercase",
            lineHeight: "36px",
            p: 0,
            pl: 1,
            pr: 4,
            "&:focus::after": {
              content: `""`,
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              borderRadius: 0.5,
              boxShadow: (theme) =>
                `inset 0px 0px 2px 1px ${theme.palette.primary.light}`,
            },
          },
        }}
      >
        <MenuItem value={SearchScope.Organization}>
          Search in Organization
        </MenuItem>
        {showSpaceOption && (
          <MenuItem value={SearchScope.Space}>Search in Space</MenuItem>
        )}
        {showWorkspaceOption && (
          <MenuItem value={SearchScope.Workspace}>Search in Workspace</MenuItem>
        )}
      </Select>
    </InputAdornment>
  );

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box id="search-dropdown" sx={{ display: "inline-flex" }}>
        <GlobalSearchIcon {...{ handleToggleFocus, isFocused }} />
        <Box
          layoutId="search"
          component={motion.div}
          variants={variants}
          initial="closed"
          animate={isFocused ? "open" : "closed"}
          id="app-search-container"
          sx={{
            position: "absolute",
            right: 8,
            top: navDrawer.navbarHeight,
            zIndex: (theme) => theme.zIndex.modal,
            border: "1px solid",
            borderColor: (theme) => theme.palette.primary.main,
            borderRadius: 1,
            overflow: "hidden",
            boxShadow: (theme) => theme.shadows[8],
          }}
        >
          <Box id="app-search-query" sx={{ position: "relative" }}>
            <TextField
              id="app-search-input"
              inputRef={inputRef}
              placeholder={`Search in ${capitalize(scope.toLowerCase())}`}
              variant="filled"
              size="small"
              hiddenLabel
              fullWidth
              disabled={isFocused ? false : true}
              slotProps={{
                input: {
                  endAdornment,
                  disableUnderline: true,
                  sx: {
                    "&.MuiInputBase-root": {
                      backgroundColor: "background.default",
                      borderRadius: 0,
                      fontSize: "14px",
                      border: "none",
                      pr: 0,
                    },
                  },
                },
              }}
              value={query}
              onFocus={handleFocus}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
            />
          </Box>
          <KeyboardHints showTabHint={false} />
        </Box>
      </Box>
    </ClickAwayListener>
  );
}
